import * as THREE from 'three'
import {
  deslocamentoNoPonto,
  type CamadaDeformacao,
  type EstadoDeformacaoVeiculo,
} from '../../dominio/didatico/campoDeformacao'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'

/**
 * Aplicação do campo de deformação nos vértices de uma malha.
 *
 * Regras que garantem timeline determinística e veículos independentes:
 *
 * 1. a geometria BASE nunca é alterada — pode estar num cache compartilhado
 *    por todos os veículos do mesmo perfil;
 * 2. cada peça deformada de cada veículo tem o SEU clone;
 * 3. o clone é sempre reescrito a partir da base (posição original + campo),
 *    então voltar a timeline, pausar ou reiniciar nunca acumula deformação.
 *
 * O campo trabalha no referencial do veículo; a malha, no referencial dela.
 * `TransformacaoRepouso` é a pose da malha dentro do veículo com as peças
 * fechadas (capô baixado, porta fechada) — o estado em que a colisão ocorre.
 */
export interface TransformacaoRepouso {
  posicao: readonly [number, number, number]
  rotacao?: readonly [number, number, number]
  quaternion?: readonly [number, number, number, number]
}

export function quaternionDeRepouso(t: TransformacaoRepouso): THREE.Quaternion {
  if (t.quaternion) return new THREE.Quaternion(...t.quaternion)
  if (t.rotacao) return new THREE.Quaternion().setFromEuler(new THREE.Euler(...t.rotacao))
  return new THREE.Quaternion()
}

export function aplicarCampoNaGeometria(
  base: THREE.BufferGeometry,
  destino: THREE.BufferGeometry,
  estado: EstadoDeformacaoVeiculo,
  perfil: PerfilVeiculo,
  camada: CamadaDeformacao,
  repouso: TransformacaoRepouso,
): void {
  const origem = base.getAttribute('position') as THREE.BufferAttribute
  const alvo = destino.getAttribute('position') as THREE.BufferAttribute
  const q = quaternionDeRepouso(repouso)
  const qInversa = q.clone().invert()
  const [px, py, pz] = repouso.posicao
  const v = new THREE.Vector3()
  const d = new THREE.Vector3()
  const ponto = { x: 0, y: 0, z: 0 }
  let maiorDeslocamento = 0

  for (let i = 0; i < origem.count; i += 1) {
    v.set(origem.getX(i), origem.getY(i), origem.getZ(i))
    const x0 = v.x
    const y0 = v.y
    const z0 = v.z
    v.applyQuaternion(q)
    ponto.x = v.x + px
    ponto.y = v.y + py
    ponto.z = v.z + pz
    const desl = deslocamentoNoPonto(estado, perfil, ponto, camada)
    d.set(desl.x, desl.y, desl.z).applyQuaternion(qInversa)
    alvo.setXYZ(i, x0 + d.x, y0 + d.y, z0 + d.z)
    maiorDeslocamento = Math.max(maiorDeslocamento, d.length())
  }
  alvo.needsUpdate = true
  // marca usada pelo destaque de inspeção: só conta o que de fato se moveu
  destino.userData.deslocamentoMaximoM = maiorDeslocamento
  destino.userData.deformado = maiorDeslocamento > 0.01
  destino.computeVertexNormals()
  destino.computeBoundingBox()
  destino.computeBoundingSphere()
}

/**
 * Cache de geometrias BASE por chave (perfil + dimensões).
 *
 * São poucas chaves fixas (dois perfis, um punhado de peças), então o mapa
 * não cresce. Quem precisa deformar clona — nunca escreve aqui.
 */
const BASES = new Map<string, THREE.BufferGeometry>()

export function geometriaBase<T extends THREE.BufferGeometry>(chave: string, criar: () => T): T {
  const existente = BASES.get(chave)
  if (existente) return existente as T
  const criada = criar()
  BASES.set(chave, criada)
  return criada
}

/**
 * Caixa subdividida com quinas arredondadas.
 *
 * Substitui a `RoundedBox` onde a peça pode deformar: a do drei concentra os
 * vértices nas quinas e deixa as faces planas sem subdivisão, e o campo não
 * teria onde dobrar a chapa.
 */
export function criarCaixaArredondada(
  largura: number,
  altura: number,
  profundidade: number,
  segmentos: readonly [number, number, number],
  raio: number,
): THREE.BoxGeometry {
  const geometria = new THREE.BoxGeometry(largura, altura, profundidade, ...segmentos)
  const r = Math.min(raio, largura / 2, altura / 2, profundidade / 2)
  if (r <= 0) return geometria
  const meia = [largura / 2 - r, altura / 2 - r, profundidade / 2 - r]
  const posicoes = geometria.getAttribute('position') as THREE.BufferAttribute
  const p = [0, 0, 0]
  const interno = [0, 0, 0]
  for (let i = 0; i < posicoes.count; i += 1) {
    p[0] = posicoes.getX(i)
    p[1] = posicoes.getY(i)
    p[2] = posicoes.getZ(i)
    let comprimento = 0
    for (let eixo = 0; eixo < 3; eixo += 1) {
      interno[eixo] = Math.max(-meia[eixo]!, Math.min(meia[eixo]!, p[eixo]!))
      comprimento += (p[eixo]! - interno[eixo]!) ** 2
    }
    comprimento = Math.sqrt(comprimento)
    if (comprimento < 1e-9) continue
    posicoes.setXYZ(
      i,
      interno[0]! + ((p[0]! - interno[0]!) / comprimento) * r,
      interno[1]! + ((p[1]! - interno[1]!) / comprimento) * r,
      interno[2]! + ((p[2]! - interno[2]!) / comprimento) * r,
    )
  }
  posicoes.needsUpdate = true
  geometria.computeVertexNormals()
  return geometria
}

/**
 * Forma com as arestas longas subdivididas: a extrusão só tem vértices no
 * contorno, e uma lateral com um vértice a cada meio metro não tem como
 * encurtar o para-lama sem esticar a porta junto.
 */
export function densificarForma(forma: THREE.Shape, passo: number, divisoesCurva = 12): THREE.Shape {
  const { shape: contorno, holes: furos } = forma.extractPoints(divisoesCurva)
  const densificar = (pontos: THREE.Vector2[]): THREE.Vector2[] => {
    const saida: THREE.Vector2[] = []
    for (let i = 0; i < pontos.length; i += 1) {
      const a = pontos[i]!
      const b = pontos[(i + 1) % pontos.length]!
      saida.push(a.clone())
      if (i === pontos.length - 1 && a.distanceTo(b) < 1e-9) continue
      const n = Math.floor(a.distanceTo(b) / passo)
      for (let k = 1; k < n; k += 1) saida.push(a.clone().lerp(b, k / n))
    }
    // o contorno fechado repete o primeiro ponto no fim
    if (saida.length > 1 && saida[0]!.distanceTo(saida[saida.length - 1]!) < 1e-9) saida.pop()
    return saida
  }
  const densa = new THREE.Shape(densificar(contorno))
  densa.holes = furos.map((furo) => new THREE.Path(densificar(furo)))
  return densa
}
